import React from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Chip,
    LinearProgress,
    Grid,
    Divider,
    Alert
} from '@mui/material';
import {
    Psychology,
    Audiotrack,
    GraphicEq,
    MusicNote,
    TrendingUp,
    Speed,
    VolumeUp,
    Equalizer,
    Timeline,
    Insights
} from '@mui/icons-material';
import { GEMSAnalysis } from '../hooks/useGEMSAnalysis';

// Helper function for formatting numbers
const formatNumber = (num?: number): string => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

// Helper for formatting percentages
const formatPercentage = (value: number): string => {
    return `${Math.round(value * 100)}%`;
};

interface EmotionalIntelligenceProps {
    gemsData: GEMSAnalysis | null;
    loading: boolean;
}

export const EmotionalIntelligenceComponent: React.FC<EmotionalIntelligenceProps> = ({ gemsData, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎭 Emotional Intelligence (GEMS Analysis)
                    </Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (!gemsData) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎭 Emotional Intelligence (GEMS Analysis)
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        No GEMS emotional analysis available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const emotions = [
        { name: gemsData.emotion_1, score: gemsData.emotion_1_score, color: 'primary' as const },
        { name: gemsData.emotion_2, score: gemsData.emotion_2_score, color: 'secondary' as const },
        { name: gemsData.emotion_3, score: gemsData.emotion_3_score, color: 'success' as const },
        { name: gemsData.emotion_4, score: gemsData.emotion_4_score, color: 'warning' as const },
    ].filter(emotion => emotion.name && emotion.score > 0);

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Psychology color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Emotional Intelligence (GEMS Analysis)
                    </Typography>
                </Box>

                {/* Emotional Signature */}
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>Emotional Signature:</strong> {gemsData.emotional_signature}
                </Alert>

                {/* Individual Emotions */}
                <Grid container spacing={2}>
                    {emotions.map((emotion, index) => (
                        <Grid item xs={12} sm={6} key={emotion.name}>
                            <Box mb={2}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="body1" fontWeight="medium" textTransform="capitalize">
                                        {emotion.name}
                                    </Typography>
                                    <Chip 
                                        label={formatPercentage(emotion.score)}
                                        color={emotion.color}
                                        size="small"
                                        variant="filled"
                                    />
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={emotion.score * 100}
                                    sx={{ 
                                        height: 8, 
                                        borderRadius: 4,
                                        backgroundColor: 'grey.200',
                                    }}
                                    color={emotion.color}
                                />
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                {/* Analysis Date */}
                <Box mt={2} pt={1} borderTop={1} borderColor="grey.200">
                    <Typography variant="caption" color="textSecondary">
                        Analyzed: {gemsData.analyzed_at.toLocaleDateString()}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

interface SonicSignatureProps {
    gemsData: GEMSAnalysis | null;
    loading: boolean;
}

export const SonicSignatureComponent: React.FC<SonicSignatureProps> = ({ gemsData, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎵 Sonic Signature
                    </Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (!gemsData) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎵 Sonic Signature
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        No sonic analysis available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Audiotrack color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Sonic Signature
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Sonic Description */}
                    <Grid item xs={12} md={6}>
                        <Box mb={2}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Signature Characteristics
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                                {gemsData.sonic_signature_text.split(',').map((characteristic, index) => (
                                    <Chip 
                                        key={index}
                                        label={characteristic.trim()}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                        </Box>

                        {gemsData.distinctive_features_text && (
                            <Box mb={2}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Distinctive Features
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {gemsData.distinctive_features_text}
                                </Typography>
                            </Box>
                        )}
                    </Grid>

                    {/* Audio Characteristics */}
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Audio Characteristics
                        </Typography>
                        
                        <Box display="flex" flexWrap="wrap" gap={2}>
                            <Box>
                                <Typography variant="caption" color="textSecondary">
                                    Bass Character
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {gemsData.bass_character || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="textSecondary">
                                    Brightness
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {gemsData.brightness_character || 'N/A'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="caption" color="textSecondary">
                                    Tonal Balance
                                </Typography>
                                <Typography variant="body2" fontWeight="medium">
                                    {gemsData.tonal_balance || 'N/A'}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                {/* Overall Description */}
                {gemsData.overall_description && (
                    <Box mt={2} pt={2} borderTop={1} borderColor="grey.200">
                        <Typography variant="body2" fontStyle="italic" color="textSecondary">
                            "{gemsData.overall_description}"
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
};

interface AudioFeaturesProps {
    gemsData: GEMSAnalysis | null;
    loading: boolean;
}

export const AudioFeaturesComponent: React.FC<AudioFeaturesProps> = ({ gemsData, loading }) => {
    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎼 Audio Features
                    </Typography>
                    <LinearProgress />
                </CardContent>
            </Card>
        );
    }

    if (!gemsData) {
        return (
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        🎼 Audio Features
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        No audio feature data available
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const frequencyBands = [
        { name: 'Sub Bass', value: gemsData.sub_ratio, color: 'error' as const },
        { name: 'Bass', value: gemsData.bass_ratio, color: 'warning' as const },
        { name: 'Low-Mid', value: gemsData.low_mid_ratio, color: 'primary' as const },
        { name: 'Mid', value: gemsData.mid_ratio, color: 'info' as const },
        { name: 'High-Mid', value: gemsData.high_mid_ratio, color: 'secondary' as const },
        { name: 'Presence', value: gemsData.presence_ratio, color: 'success' as const },
        { name: 'Air', value: gemsData.air_ratio, color: 'inherit' as const },
    ];

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <GraphicEq color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Audio Features
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    {/* Core Metrics */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Core Metrics
                        </Typography>
                        
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">BPM:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {Math.round(gemsData.bpm)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Key:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {gemsData.key} {gemsData.scale}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Energy:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatPercentage(gemsData.energy)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Danceability:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatPercentage(gemsData.danceability)}
                                </Typography>
                            </Box>
                        </Box>

                        <Box mt={2}>
                            <Typography variant="caption" color="textSecondary">
                                Tempo: {gemsData.tempo_description}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="textSecondary">
                                Dance: {gemsData.danceability_description}
                            </Typography>
                        </Box>
                    </Grid>

                    {/* Frequency Analysis */}
                    <Grid item xs={12} md={8}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Frequency Analysis
                        </Typography>
                        
                        {frequencyBands.map((band) => (
                            <Box key={band.name} mb={1}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                    <Typography variant="body2">{band.name}</Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        {formatPercentage(band.value)}
                                    </Typography>
                                </Box>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={band.value * 100}
                                    sx={{ height: 6, borderRadius: 3 }}
                                    color={band.color}
                                />
                            </Box>
                        ))}
                    </Grid>
                </Grid>

                {/* Advanced Metrics */}
                <Box mt={3} pt={2} borderTop={1} borderColor="grey.200">
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Advanced Analysis
                    </Typography>
                    
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Loudness (LUFS)
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {gemsData.lufs_integrated.toFixed(1)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Dynamic Range
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {gemsData.dynamic_range.toFixed(1)} dB
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Complexity
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {formatPercentage(gemsData.spectral_complexity)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Genre Fit
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                                {formatPercentage(gemsData.genre_positioning_score)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Genre Information */}
                <Box mt={2} display="flex" gap={1} alignItems="center">
                    <Chip 
                        label={gemsData.primary_genre}
                        color="primary"
                        size="small"
                    />
                    {gemsData.secondary_genre && (
                        <Chip 
                            label={gemsData.secondary_genre}
                            color="secondary"
                            size="small"
                            variant="outlined"
                        />
                    )}
                    {gemsData.is_instrumental && (
                        <Chip 
                            label="Instrumental"
                            color="info"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};